// TimesheetPage (timesheet rules §4): one person × one pay period. PageHead (the crumb back to Hours,
// the person and the period, the pay line and the figures once each); To fix, only while something's
// left (ToFixList: each first fix opens the quick edit with it chosen); the days (PeriodDays: one
// timeline, a row per day, weeks with past 40 h); the breakdown (KeyValue: nothing from the facts
// again); the changes (HistoryFeed of edits, an area like the rest even when there are none); and the
// lock line, written for the viewer. Loading, a failed read and an old link for someone it isn't for
// say so in words instead. Until there's a pay period (QA-757) it's a week: ‹ This week › instead of the
// periods, one quiet line where the pay line goes, and no lock line. A period past the hours imported
// from the earlier system says so over its days (QA-760). Minutes only, never pay. Reads only
// --tspage-*; what it's built from reads its own.
import { Area } from '../../components/Area/Area.tsx';
import { SelectField } from '../../components/Field/Field.tsx';
import { Figures } from '../../components/Figures/Figures.tsx';
import { HistoryFeed } from '../../components/HistoryFeed/HistoryFeed.tsx';
import { KeyValue } from '../../components/KeyValue/KeyValue.tsx';
import { Link } from '../../components/Link/Link.tsx';
import { Segmented, type Segment } from '../../components/Segmented/Segmented.tsx';
import { PageHead, type Crumb } from '../../patterns/PageHead/PageHead.tsx';
import { CHANGE_FILTERS, changeEntries } from '../changes.tsx';
import { payLineOf, periodFigures, WeekStep, type WeekStepProps } from '../pay-period.tsx';
import { PeriodDays } from '../PeriodDays/PeriodDays.tsx';
import type { TimesheetView, ToFixRow } from '../timesheet-view.ts';
import { ToFixList } from '../ToFixList/ToFixList.tsx';
import './TimesheetPage.css';

/** Instead of the page: still loading, a read that failed, or an old link for someone it isn't for. */
export interface PageNotice {
  title: string;
  words: string;
  /** "Try again", "The board ›". */
  action?: { label: string; onPress: () => void };
}

export interface TimesheetPageProps {
  view: TimesheetView | null;
  notice?: PageNotice;
  /** The company's today ("2026-10-25"): the changes' Today. */
  today: string;
  people: { value: string; label: string }[];
  staffId: string;
  onPerson: (staffId: string) => void;
  /** The pay periods near this one, newest last; the one shown is on. */
  periods: Segment[];
  periodId: string;
  onPeriod: (periodId: string) => void;
  /** A To fix row's first fix, or a day's word (fixId null): the quick edit on that day. */
  onOpenDay: (date: string, fixId: string | null) => void;
  /** The crumb's Hours. */
  onHours: () => void;
  /** No pay period yet: the weeks instead of the periods. */
  week?: WeekStepProps;
  /** No pay period yet: the quiet line's Set up payroll › (Books › Payroll), for those who may. */
  onSetUpPayroll?: () => void;
}

export function TimesheetPage(props: TimesheetPageProps) {
  const { view, notice, today, onOpenDay, onHours } = props;
  const crumb = (name: string): Crumb[] => [{ label: 'Hours', onPress: onHours }, { label: name }];
  if (!view || notice)
    return (
      <div className="fl-tspage">
        <PageHead crumb={crumb('Timesheet')} title={notice?.title ?? 'Timesheet'} gaps="wide" />
        <p className="fl-tspage__notice" role="status">
          {notice?.words ?? 'Loading the timesheet…'}{' '}
          {notice?.action ? <Link onPress={notice.action.onPress}>{notice.action.label}</Link> : null}
        </p>
      </div>
    );
  const name = view.crumb.split(' › ').slice(1).join(' › ');
  const open = (r: ToFixRow, fix: boolean) => onOpenDay(r.date, fix ? r.fix.fixId : null);
  return (
    <div className="fl-tspage">
      <PageHead
        crumb={crumb(name)}
        title={view.title}
        lede={payLineOf(view.payLine, view.noPayroll, props.onSetUpPayroll)}
        {...(view.facts.length ? { figures: <Figures items={periodFigures(view.facts)} /> } : {})}
        gaps="wide"
        titleId="tspage-title"
        actions={
          <>
            <SelectField
              label="Person"
              value={props.staffId}
              options={props.people}
              onChange={props.onPerson}
            />
            {props.week ? (
              <WeekStep {...props.week} />
            ) : (
              <Segmented
                label="Pay period"
                items={props.periods}
                selectedKey={props.periodId}
                onSelectionChange={props.onPeriod}
              />
            )}
          </>
        }
      />
      {view.toFix.length ? (
        <Area title="To fix" count={view.toFix.length} defaultOpen>
          <ToFixList rows={view.toFix} onFix={(r) => open(r, true)} onOpenDay={(r) => open(r, false)} />
        </Area>
      ) : null}
      <Area title="Days" defaultOpen>
        {view.importNote ? <p className="fl-tspage__import">{view.importNote}</p> : null}
        <PeriodDays days={view.days} weeks={view.weeks} onOpenDay={(d) => onOpenDay(d, null)} />
      </Area>
      <Area title="Breakdown" defaultOpen>
        <KeyValue items={view.breakdown} label="Breakdown" />
      </Area>
      {/* opens and closes like the areas above it, so its name lines up with theirs, chevron and all */}
      <Area title="Changes" count={view.changes.length} defaultOpen>
        {view.changes.length ? (
          <HistoryFeed
            entries={changeEntries(view.changes)}
            filters={CHANGE_FILTERS}
            today={today}
            searchLabel="Search the changes"
          />
        ) : (
          <p className="fl-tspage__none">No changes yet.</p>
        )}
      </Area>
      {view.lockLine ? <p className="fl-tspage__lock">{view.lockLine}</p> : null}
    </div>
  );
}
